import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { useCompanyContext } from "../../common/company/CompanyContext";
import {
    importChartOfAccounts,
    importLedgerLines,
    fetchImports,
    removeImport,
    fetchAccountCodes,
    fetchLedgerLines,
    fetchMappings,
    searchEsgItems,
    setMapping,
    removeMapping,
    setFigureMapping,
} from "./accountLedgerService";
import ToggleSection from "../../common/toggle/ToggleSection";
import ToolLogoStrip from "../../common/toggle/ToolLogoStrip";
import { PILLAR_COLORS } from "../../site-admin/report_management/blueprint/blueprintReportingTemplates";
import { ACCOUNTING_TOOLS, LEDGER_FIGURE_TOPICS, figureByPath } from "./ledgerFiguresDTO";
import LedgerConnectorsSection from "./LedgerConnectorsSection";
import LedgerFiguresSection from "./LedgerFiguresSection";

/**
 * 📒 ACCOUNTS & LEDGER (company screen, finance user)
 * Same layout as HR & Workforce — three sections behind charcoal toggle bars,
 * all closed, Environmental accent:
 *   • Accounting connectors (switched on by a global admin)
 *   • Map your account codes — import the chart of accounts and ledger, then say
 *     which reporting figure each code counts towards (automated values) and which
 *     ESG taxonomy items it feeds
 *   • Enter figures manually — per reporting period
 */
const ACCENT = PILLAR_COLORS.E.main;
const lastYear = () => { const y = new Date().getFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31` }; };
const g = (o, k) => GoldenSourceHelper.get(o, k);

// Fields we read from an import file, with header names we auto-recognise.
const FIELDS = {
    COA: [
        { key: "code", label: "Account code", required: true, guess: /^(account|acc|gl|nominal)?\s*(code|no|number|#)$|^account$/i },
        { key: "description", label: "Description", guess: /desc|name|title/i },
        { key: "category", label: "Category / group", guess: /categ|group|type|class/i },
    ],
    LEDGER: [
        { key: "code", label: "Account code", required: true, guess: /^(account|acc|gl|nominal)?\s*(code|no|number|#)$|^account$/i },
        { key: "postingDate", label: "Date", guess: /date/i },
        { key: "description", label: "Description", guess: /desc|narrat|memo|text|detail/i },
        { key: "supplier", label: "Supplier", guess: /supplier|vendor|creditor|payee/i },
        { key: "reference", label: "Reference", guess: /ref|invoice|doc/i },
        { key: "costCentre", label: "Cost centre / site", guess: /cost\s*cent|site|dept|department|location/i },
        { key: "amount", label: "Amount", guess: /amount|net|value|debit|total/i },
        { key: "currency", label: "Currency", guess: /curr|ccy/i },
        { key: "quantity", label: "Quantity", guess: /qty|quant|volume|litre|liter|kwh/i },
        { key: "unit", label: "Unit", guess: /^unit|uom|measure/i },
    ],
};

const money = (v) => (v == null ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const day = (v) => (v ? String(v).slice(0, 10) : "");

const toIsoDate = (v) => {
    if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
    if (typeof v === "number") { const d = XLSX.SSF.parse_date_code(v); return d ? `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}` : null; }
    const s = String(v || "").trim();
    const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/); // dd/mm/yyyy
    if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    return s || null;
};

const toNumber = (v) => {
    if (v == null || v === "") return null;
    if (typeof v === "number") return v;
    const n = Number(String(v).replace(/[^\d.,-]/g, "").replace(/,(?=\d{3}\b)/g, "").replace(",", "."));
    return isNaN(n) ? null : n;
};

export default function AccountLedgerView() {
    const navigate = useNavigate();
    const { activeCompanyId, activeCompanyName } = useCompanyContext();
    const companyId = activeCompanyId ? Number(activeCompanyId) : null;

    const [codes, setCodes] = useState([]);
    const [imports, setImports] = useState([]);
    const [filter, setFilter] = useState("ALL");
    const [search, setSearch] = useState("");

    const [file, setFile] = useState(null); // { kind, name, headers, rows }
    const [columns, setColumns] = useState({});
    const [progress, setProgress] = useState("");

    const [selected, setSelected] = useState(null);
    const [lines, setLines] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [itemSearch, setItemSearch] = useState("");
    const [itemHits, setItemHits] = useState([]);
    const [draft, setDraft] = useState({ basis: "QUANTITY", factor: "", note: "" });

    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");

    const [period, setPeriod] = useState(lastYear());
    const [figureDraft, setFigureDraft] = useState({ path: "", basis: "QUANTITY", factor: "" });
    const [figuresVersion, setFiguresVersion] = useState(0);

    const run = async (fn, ok) => {
        setBusy(true);
        setError("");
        try {
            const r = await fn();
            if (ok) setNotice(typeof ok === "function" ? ok(r) : ok);
            return r;
        } catch (e) {
            setError(e.message || String(e));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const reload = useCallback(async () => {
        if (!companyId) return;
        const [c, i] = await Promise.all([fetchAccountCodes(companyId), fetchImports(companyId)]);
        setCodes(c);
        setImports(i);
    }, [companyId]);

    useEffect(() => { run(reload); }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------- import ----------
    const pickFile = (kind) => async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        await run(async () => {
            const wb = XLSX.read(await f.arrayBuffer(), { type: "array", cellDates: true });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
            if (!rows.length) throw new Error("That file has no rows.");
            const headers = Object.keys(rows[0]);
            const guessed = {};
            FIELDS[kind].forEach((fd) => {
                const h = headers.find((x) => fd.guess.test(String(x).trim()) && !Object.values(guessed).includes(x));
                if (h) guessed[fd.key] = h;
            });
            setColumns(guessed);
            setFile({ kind, name: f.name, headers, rows });
        });
    };

    const shapedRows = useMemo(() => {
        if (!file) return [];
        return file.rows.map((r) => {
            const out = {};
            FIELDS[file.kind].forEach(({ key }) => {
                const col = columns[key];
                if (!col) return;
                let v = r[col];
                if (key === "postingDate") v = toIsoDate(v);
                else if (key === "amount" || key === "quantity") v = toNumber(v);
                else v = v === "" || v == null ? null : String(v).trim();
                out[key] = v;
            });
            return out;
        }).filter((r) => r.code);
    }, [file, columns]);

    const doImport = () =>
        run(async () => {
            const fn = file.kind === "COA" ? importChartOfAccounts : importLedgerLines;
            const res = await fn(companyId, file.name, shapedRows, (i, n) => setProgress(n > 1 ? `Sending part ${i} of ${n}…` : ""));
            setProgress("");
            setFile(null);
            await reload();
            return res;
        }, () => `Imported ${shapedRows.length} rows from ${file.name}.`);

    const dropImport = (batchId) =>
        run(async () => { await removeImport(batchId); await reload(); }, "Import removed (its ledger lines are gone; account codes stay).");

    // ---------- code detail ----------
    const openCode = async (row) => {
        setSelected(row);
        setItemHits([]);
        setItemSearch("");
        const code = g(row, "code");
        const [l, m] = await Promise.all([fetchLedgerLines(companyId, code), fetchMappings(companyId, code)]);
        setLines(l);
        setMappings(m);
        setDraft({ basis: g(row, "totalQuantity") ? "QUANTITY" : "SPEND", factor: "", note: "" });
        setFigureDraft({
            path: g(row, "figurePath") || "",
            basis: g(row, "figureBasisCode") || (g(row, "totalQuantity") ? "QUANTITY" : "SPEND"),
            factor: g(row, "figureFactor") != null ? String(Number(g(row, "figureFactor"))) : "",
        });
    };

    const saveFigure = () =>
        run(async () => {
            const code = g(selected, "code");
            await setFigureMapping(companyId, code, figureDraft.path, figureDraft.basis, figureDraft.factor);
            await reload();
            setFiguresVersion((n) => n + 1);
        }, () => (figureDraft.path ? `${g(selected, "code")} now counts towards ${figureByPath(figureDraft.path)?.label}.` : `${g(selected, "code")} no longer counts towards a figure.`));

    const findItems = () => run(async () => setItemHits(await searchEsgItems(itemSearch)));

    const addMapping = (item) =>
        run(async () => {
            const code = g(selected, "code");
            await setMapping(companyId, code, g(item, "taxonomyDetailRowId"), draft.basis, draft.factor, draft.note);
            setMappings(await fetchMappings(companyId, code));
            setItemHits([]);
            setItemSearch("");
            await reload();
        }, "Mapped.");

    const dropMapping = (id) =>
        run(async () => {
            await removeMapping(id);
            setMappings(await fetchMappings(companyId, g(selected, "code")));
            await reload();
        }, "Mapping removed.");

    // ---------- summary + filtering ----------
    const stats = useMemo(() => {
        const mapped = codes.filter((c) => g(c, "mappingCount") > 0);
        const unmapped = codes.filter((c) => !g(c, "mappingCount"));
        const sum = (arr) => arr.reduce((t, c) => t + (Number(g(c, "totalAmount")) || 0), 0);
        return { total: codes.length, mapped: mapped.length, unmapped: unmapped.length, unmappedSpend: sum(unmapped), mappedSpend: sum(mapped) };
    }, [codes]);

    const visible = codes.filter((c) => {
        if (filter === "MAPPED" && !g(c, "mappingCount")) return false;
        if (filter === "UNMAPPED" && g(c, "mappingCount")) return false;
        if (!search) return true;
        return `${g(c, "code")} ${g(c, "description")} ${g(c, "mappedTo") || ""}`.toLowerCase().includes(search.toLowerCase());
    });

    if (!companyId) {
        return (
            <div className="page-main"><div className="page-main-inner"><div className="page-section">
                <p className="text-muted">Choose a company on the dashboard first.</p>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/registered/dashboard")}>Back to dashboard</button>
            </div></div></div>
        );
    }

    return (
        <div className="page-main">
            <div className="page-main-inner">
                <div className="page-section">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h1 className="h4 fw-bold mb-1">Accounts &amp; Ledger — {activeCompanyName}</h1>
                            <p className="text-muted small mb-0">
                                Bring in your chart of accounts and ledger, then tell us which ESG item each account code feeds.
                                Nothing can be reported from an account code until it is mapped.
                            </p>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/registered/company-dashboard")}>Back</button>
                    </div>

                    <ToolLogoStrip tools={ACCOUNTING_TOOLS} title="Connects with your accounting package" />

                    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                        <div>
                            <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="al-from">Reporting period — from</label>
                            <input id="al-from" type="date" className="form-control form-control-sm" value={period.from}
                                   onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} />
                        </div>
                        <div>
                            <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="al-to">to</label>
                            <input id="al-to" type="date" className="form-control form-control-sm" value={period.to}
                                   onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} />
                        </div>
                    </div>

                    <ToggleSection accent={ACCENT} title="Accounting connectors">
                        <LedgerConnectorsSection />
                    </ToggleSection>

                    <ToggleSection accent={ACCENT} title="Map your account codes"
                                   aside={`${stats.mapped}/${stats.total} mapped · ${codes.filter((c) => g(c, "figurePath")).length} feed a figure`}>
                    {/* summary */}
                    <div className="row g-2 mb-3">
                        {[
                            ["Account codes", stats.total, ""],
                            ["Mapped", stats.mapped, "text-success"],
                            ["Not mapped", stats.unmapped, stats.unmapped ? "text-danger" : ""],
                            ["Spend not mapped", money(stats.unmappedSpend), stats.unmappedSpend ? "text-danger" : ""],
                        ].map(([label, val, cls]) => (
                            <div key={label} className="col-6 col-md-3">
                                <div className="border rounded p-2">
                                    <div className="extra-small text-muted text-uppercase fw-bold">{label}</div>
                                    <div className={`h5 mb-0 fw-bold ${cls}`}>{val}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {notice && <div className="alert alert-success py-1 small mb-2" onClick={() => setNotice("")}>{notice}</div>}
                    {error && <div className="alert alert-danger py-1 small mb-2" onClick={() => setError("")}>{error}</div>}

                    {/* import */}
                    <div className="border rounded p-2 mb-3">
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                            <span className="extra-small fw-bold text-uppercase text-primary me-2">Import</span>
                            <label className="btn btn-sm btn-outline-primary mb-0">
                                Chart of accounts (CSV / Excel)
                                <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={pickFile("COA")} />
                            </label>
                            <label className="btn btn-sm btn-outline-primary mb-0">
                                Ledger postings (CSV / Excel)
                                <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={pickFile("LEDGER")} />
                            </label>
                            {imports.length > 0 && (
                                <span className="extra-small text-muted ms-auto">{imports.length} file(s) imported</span>
                            )}
                        </div>

                        {file && (
                            <div className="mt-3">
                                <div className="small fw-semibold mb-2">
                                    {file.kind === "COA" ? "Chart of accounts" : "Ledger"}: {file.name} — {file.rows.length} rows.
                                    Tell us which column is which:
                                </div>
                                <div className="row g-2 mb-2">
                                    {FIELDS[file.kind].map((fd) => (
                                        <div key={fd.key} className="col-6 col-md-3">
                                            <div className="extra-small fw-bold text-muted mb-1">{fd.label}{fd.required ? " *" : ""}</div>
                                            <select className="form-select form-select-sm" value={columns[fd.key] || ""}
                                                    onChange={(e) => setColumns({ ...columns, [fd.key]: e.target.value || undefined })}>
                                                <option value="">— not in file —</option>
                                                {file.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                                <div className="table-responsive mb-2">
                                    <table className="table table-sm small mb-0">
                                        <thead><tr>{FIELDS[file.kind].filter((f) => columns[f.key]).map((f) => <th key={f.key}>{f.label}</th>)}</tr></thead>
                                        <tbody>
                                            {shapedRows.slice(0, 5).map((r, i) => (
                                                <tr key={i}>{FIELDS[file.kind].filter((f) => columns[f.key]).map((f) => <td key={f.key}>{String(r[f.key] ?? "")}</td>)}</tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <button className="btn btn-sm btn-primary fw-bold" disabled={busy || !columns.code || !shapedRows.length} onClick={doImport}>
                                        Import {shapedRows.length} rows
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setFile(null)}>Cancel</button>
                                    {progress && <span className="extra-small text-muted">{progress}</span>}
                                </div>
                            </div>
                        )}

                        {!file && imports.length > 0 && (
                            <details className="mt-2">
                                <summary className="extra-small text-muted">Imported files</summary>
                                {imports.map((b) => (
                                    <div key={g(b, "batchId")} className="d-flex small py-1 border-bottom">
                                        <span className="badge bg-light text-dark me-2">{g(b, "kindCode") === "COA" ? "Chart" : "Ledger"}</span>
                                        <span className="flex-grow-1">{g(b, "fileName")} — {g(b, "rowCount")} rows
                                            {g(b, "periodFrom") ? ` · ${day(g(b, "periodFrom"))} to ${day(g(b, "periodTo"))}` : ""}</span>
                                        <button className="btn btn-link btn-sm p-0 text-danger" disabled={busy} onClick={() => dropImport(g(b, "batchId"))}>Remove</button>
                                    </div>
                                ))}
                            </details>
                        )}
                    </div>

                    <div className="row g-3">
                        {/* codes */}
                        <div className={selected ? "col-lg-6" : "col-12"}>
                            <div className="d-flex gap-2 mb-2">
                                <div className="btn-group btn-group-sm">
                                    {[["ALL", "All"], ["UNMAPPED", "Not mapped"], ["MAPPED", "Mapped"]].map(([k, l]) => (
                                        <button key={k} className={`btn ${filter === k ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setFilter(k)}>{l}</button>
                                    ))}
                                </div>
                                <input className="form-control form-control-sm" placeholder="Search code, description or ESG item"
                                       value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                            <div className="table-responsive border rounded" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                <table className="table table-sm table-hover small mb-0">
                                    <thead className="table-light"><tr>
                                        <th>Code</th><th>Description</th><th className="text-end">Lines</th>
                                        <th className="text-end">Amount</th><th className="text-end">Quantity</th><th>Counts towards</th><th>Feeds ESG item</th>
                                    </tr></thead>
                                    <tbody>
                                        {visible.map((c) => (
                                            <tr key={g(c, "accountCodeId")} style={{ cursor: "pointer" }}
                                                className={selected && g(selected, "code") === g(c, "code") ? "table-primary" : ""}
                                                onClick={() => openCode(c)}>
                                                <td className="font-monospace">{g(c, "code")}{!g(c, "inChartOfAccounts") &&
                                                    <span className="badge bg-warning-subtle text-dark ms-1" title="Seen in the ledger but not in the chart of accounts">ledger only</span>}</td>
                                                <td>{g(c, "description")}</td>
                                                <td className="text-end">{g(c, "lineCount") || ""}</td>
                                                <td className="text-end">{money(g(c, "totalAmount"))}</td>
                                                <td className="text-end">{g(c, "totalQuantity") ? `${money(g(c, "totalQuantity"))} ${g(c, "units") || ""}` : ""}</td>
                                                <td>{g(c, "figurePath")
                                                    ? <span className="text-success">{figureByPath(g(c, "figurePath"))?.label || g(c, "figurePath")}</span>
                                                    : <span className="text-muted">—</span>}</td>
                                                <td>{g(c, "mappingCount")
                                                    ? <span className="text-success">{g(c, "mappedTo")}</span>
                                                    : <span className="badge bg-danger-subtle text-danger">Not mapped</span>}</td>
                                            </tr>
                                        ))}
                                        {!visible.length && (
                                            <tr><td colSpan={7} className="text-muted text-center py-3">
                                                {codes.length ? "Nothing matches." : "No account codes yet — import your chart of accounts or ledger above."}
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* detail */}
                        {selected && (
                            <div className="col-lg-6">
                                <div className="border rounded p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div className="fw-bold"><span className="font-monospace">{g(selected, "code")}</span> {g(selected, "description")}</div>
                                            <div className="extra-small text-muted">
                                                {g(selected, "lineCount") || 0} ledger lines · {money(g(selected, "totalAmount"))}
                                                {g(selected, "totalQuantity") ? ` · ${money(g(selected, "totalQuantity"))} ${g(selected, "units") || ""}` : ""}
                                            </div>
                                        </div>
                                        <button className="btn btn-sm btn-link text-muted" onClick={() => setSelected(null)}>Close</button>
                                    </div>

                                    <div className="extra-small fw-bold text-uppercase mb-1" style={{ color: ACCENT }}>Counts towards this reporting figure</div>
                                    <div className="d-flex flex-wrap gap-1 mb-1">
                                        <select className="form-select form-select-sm" style={{ minWidth: 220, flex: 1 }} value={figureDraft.path}
                                                onChange={(e) => setFigureDraft({ ...figureDraft, path: e.target.value })}>
                                            <option value="">— none —</option>
                                            {LEDGER_FIGURE_TOPICS.map((t) => (
                                                <optgroup key={t.key} label={t.name}>
                                                    {t.figures.map((f) => <option key={f.path} value={f.path}>{f.label}{f.unit ? ` (${f.unit})` : ""}</option>)}
                                                </optgroup>
                                            ))}
                                        </select>
                                        <select className="form-select form-select-sm" style={{ maxWidth: 150 }} value={figureDraft.basis}
                                                onChange={(e) => setFigureDraft({ ...figureDraft, basis: e.target.value })}>
                                            <option value="QUANTITY">from quantity</option>
                                            <option value="SPEND">from spend</option>
                                        </select>
                                        <input className="form-control form-control-sm" style={{ maxWidth: 100 }} placeholder="× factor"
                                               value={figureDraft.factor} onChange={(e) => setFigureDraft({ ...figureDraft, factor: e.target.value })} />
                                        <button className="btn btn-sm text-white" style={{ backgroundColor: ACCENT }} disabled={busy} onClick={saveFigure}>Save</button>
                                    </div>
                                    <div className="extra-small text-muted mb-3">
                                        Its ledger lines then add up automatically into that figure for each reporting period.
                                    </div>

                                    <div className="extra-small fw-bold text-uppercase text-primary mb-1">Also feeds these ESG taxonomy items</div>
                                    {mappings.map((m) => (
                                        <div key={g(m, "accountMappingId")} className="d-flex small py-1 border-bottom">
                                            <span className="flex-grow-1">
                                                {g(m, "measureName")}
                                                <span className="extra-small text-muted ms-2">
                                                    from {g(m, "valueBasisCode") === "QUANTITY" ? "quantity" : "spend"}
                                                    {g(m, "factor") != null ? ` × ${Number(g(m, "factor"))}` : ""}
                                                    {g(m, "factorNote") ? ` (${g(m, "factorNote")})` : ""}
                                                </span>
                                            </span>
                                            <button className="btn btn-link btn-sm p-0 text-danger" disabled={busy} onClick={() => dropMapping(g(m, "accountMappingId"))}>Remove</button>
                                        </div>
                                    ))}
                                    {!mappings.length && <div className="text-danger small mb-1">Not mapped yet.</div>}

                                    <div className="mt-2 mb-3">
                                        <div className="d-flex gap-1 mb-1">
                                            <input className="form-control form-control-sm" placeholder="Find an ESG item, e.g. diesel, electricity, freight, emissions"
                                                   value={itemSearch} onChange={(e) => setItemSearch(e.target.value)}
                                                   onKeyDown={(e) => e.key === "Enter" && itemSearch && findItems()} />
                                            <button className="btn btn-sm btn-outline-primary" disabled={!itemSearch || busy} onClick={findItems}>Find</button>
                                        </div>
                                        <div className="d-flex gap-1 mb-1">
                                            <select className="form-select form-select-sm" style={{ maxWidth: 170 }} value={draft.basis}
                                                    onChange={(e) => setDraft({ ...draft, basis: e.target.value })}>
                                                <option value="QUANTITY">Use quantity</option>
                                                <option value="SPEND">Use spend</option>
                                            </select>
                                            <input className="form-control form-control-sm" style={{ maxWidth: 110 }} placeholder="Factor"
                                                   value={draft.factor} onChange={(e) => setDraft({ ...draft, factor: e.target.value })} />
                                            <input className="form-control form-control-sm" placeholder="Factor note, e.g. litres diesel → MWh"
                                                   value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
                                        </div>
                                        <div className="list-group list-group-flush" style={{ maxHeight: 200, overflowY: "auto" }}>
                                            {itemHits.map((h) => (
                                                <button key={g(h, "taxonomyDetailRowId")} type="button" disabled={busy}
                                                        className="list-group-item list-group-item-action small py-1" onClick={() => addMapping(h)}>
                                                    {g(h, "measureName")}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="extra-small fw-bold text-uppercase text-primary mb-1">Ledger lines</div>
                                    <div className="table-responsive" style={{ maxHeight: 280, overflowY: "auto" }}>
                                        <table className="table table-sm small mb-0">
                                            <thead className="table-light"><tr><th>Date</th><th>Description</th><th>Supplier</th><th className="text-end">Amount</th><th className="text-end">Qty</th></tr></thead>
                                            <tbody>
                                                {lines.map((l) => (
                                                    <tr key={g(l, "ledgerLineId")}>
                                                        <td>{day(g(l, "postingDate"))}</td>
                                                        <td>{g(l, "description")}</td>
                                                        <td>{g(l, "supplier")}</td>
                                                        <td className="text-end">{money(g(l, "amount"))}</td>
                                                        <td className="text-end">{g(l, "quantity") != null ? `${money(g(l, "quantity"))} ${g(l, "unit") || ""}` : ""}</td>
                                                    </tr>
                                                ))}
                                                {!lines.length && <tr><td colSpan={5} className="text-muted">No ledger lines for this code yet.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </ToggleSection>

                    <ToggleSection accent={ACCENT} title="Enter figures manually" subtitle={`${period.from} → ${period.to}`}>
                        <LedgerFiguresSection companyId={companyId} period={period} accent={ACCENT} refreshKey={figuresVersion} />
                    </ToggleSection>
                </div>
            </div>
        </div>
    );
}
